/**
 * The 3D mesh scene viewer: an interactive three.js renderer with orbit/walk controls,
 * spatial kind visibility toggles, and optional scene object overlays (outlets, luminaires).
 * Reuses the ingest preview's lighting and control patterns (docs/ORG_TENANCY_SPEC.md).
 *
 * At depth "rows" or "whole", focuses on the scope's members; at depth "counts", shows all.
 */
import { useEffect, useRef, useState } from 'react';
import { authFetch } from '../../../api';
import { Label, Notice, Overlay, Segment, Stack, Text } from '../../../strata';
import { MeshScene, type ControlMode, type SpatialKind } from '../scene';
import { type ScopeView } from '../queries';

interface SceneViewProps {
  projectId: string;
  scopeView?: ScopeView;
}

const CONTROL_MODES = [
  { key: 'orbit', label: 'Orbit' },
  { key: 'walk', label: 'Walk' },
] as const;

export function SceneView({ projectId, scopeView }: SceneViewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<MeshScene | null>(null);
  const [controlMode, setControlMode] = useState<ControlMode>('orbit');
  const [spatialKinds, setSpatialKinds] = useState<SpatialKind[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Extract the members to show based on depth. At depth "rows" or "whole", show only
  // the scope's members; at depth "counts", show everything (no filtering).
  useEffect(() => {
    if (!sceneRef.current) return;

    let visibleMembers: Set<string> | null = null;
    if (scopeView && scopeView.depth !== 'counts') {
      if (scopeView.depth === 'rows') {
        visibleMembers = new Set(scopeView.rows.map((row) => row.origin_id));
      } else if (scopeView.depth === 'whole') {
        visibleMembers = new Set(scopeView.elements.map((el) => el.origin_id));
      }
    }
    sceneRef.current.setVisibilityByOriginIds(visibleMembers);
  }, [scopeView]);

  // Initialize scene and load GLB
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;

    const init = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        const scene = new MeshScene(canvas);
        sceneRef.current = scene;

        // Load the GLB from the server
        await scene.load(projectId, (url) => authFetch(url));

        // Classify using scope view data (at rows/whole depths) or GLB nodes (at counts depth).
        // At counts depth with no per-element rows, show no per-class layers.
        if (scopeView && scopeView.depth !== 'counts') {
          scene.classifyFromScope(scopeView);
        } else {
          // At counts depth: the GLB nodes are their own classification.
          // No per-class layers are drawn; the layer list shows children instead.
          scene.clearSpatialKinds();
        }

        if (!disposed) {
          setSpatialKinds(scene.getSpatialKinds());
        }
      } catch (error) {
        if (!disposed) {
          setLoadError(error instanceof Error ? error.message : String(error));
        }
      } finally {
        if (!disposed) {
          setIsLoading(false);
        }
      }
    };

    void init();

    // Handle window resize
    const handleResize = () => {
      if (sceneRef.current && canvas.parentElement) {
        const width = canvas.parentElement.clientWidth;
        const height = canvas.parentElement.clientHeight;
        sceneRef.current.onWindowResize(width, height);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    return () => {
      disposed = true;
      resizeObserver.disconnect();
      if (sceneRef.current) {
        sceneRef.current.dispose();
        sceneRef.current = null;
      }
    };
  }, [projectId, scopeView]);

  const handleControlModeChange = (mode: ControlMode) => {
    setControlMode(mode);
    if (sceneRef.current) {
      sceneRef.current.setControlMode(mode);
    }
  };

  const visibleKinds = spatialKinds.filter((k) => k.visible).map((k) => k.ifcClass);
  const setVisibleKinds = (next: Set<string>) => {
    for (const kind of spatialKinds) {
      const visible = next.has(kind.ifcClass);
      if (visible !== kind.visible && sceneRef.current) sceneRef.current.setVisible(kind.ifcClass, visible);
    }
    setSpatialKinds((prev) => prev.map((k) => ({ ...k, visible: next.has(k.ifcClass) })));
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />

      <Overlay anchor="top-left" dense ariaLabel="Scene controls">
        <Label>View mode</Label>
        <Segment
          size="sm"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[controlMode]}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0];
            if (key === 'orbit' || key === 'walk') handleControlModeChange(key);
          }}
          options={CONTROL_MODES}
        />

        {spatialKinds.length > 0 && (
          <Stack direction="column" gap="xs">
            <Label>Layers</Label>
            <Segment
              size="sm"
              selectionMode="multiple"
              selectedKeys={visibleKinds}
              onSelectionChange={(keys) => setVisibleKinds(new Set(Array.from(keys, String)))}
              options={spatialKinds.map((kind) => ({
                key: kind.ifcClass,
                label: `${kind.name} ×${kind.count}`,
                ariaLabel: `${kind.name}, ${kind.count} elements`,
              }))}
            />
          </Stack>
        )}

        {isLoading && (
          <Text size="caption" tone="secondary">
            Loading scene…
          </Text>
        )}
        {loadError && <Notice kind="error">{loadError}</Notice>}
      </Overlay>

      {controlMode === 'walk' && (
        <Overlay anchor="bottom-left" dense>
          <Text size="caption" tone="secondary">
            Click to look · WASD to move · ESC to exit
          </Text>
        </Overlay>
      )}
    </div>
  );
}
